import { Component, Input, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, merge, takeUntil } from 'rxjs';
import { BarcodeScannerService } from '../core/barcode-scanner/barcode-scanner.service';
import { FeedbackService } from '../core/feedback/feedback.service';
import { DataService } from '../data/data.service';
import { CartItem, FEATURE_FLAGS } from '../data/models/models';
import { PusherService } from '../data/pusher.service';
import { CustomerSelectorComponent } from './cart-customer-selector.component';
import { CartItemEditComponent } from './cart-item-edit.component';
import { CartItemAction } from './cart-item.component';
import { CouponModalComponent } from './coupon-modal.component';
import { CustomerModalComponent } from './customer-modal.component';
import { NotesModalComponent } from './notes-modal.component';

@Component({
    selector: 'app-cart-content',
    template: `
    <div class="vstack p-1 p-md-3 separated">
      <app-cart-item *ngFor="let item of dataService.cart.lineItems"
                     [item]="item"
                     [readonly]="readonly"
                     [showItemCounters]="showItemCounters"
                     (itemChange)="handleItemAction($event)"></app-cart-item>

      <div class="d-flex justify-content-between align-items-center py-1" *appFeature="FEATURE_FLAGS.COUPONS">
        <div class="d-flex flex-column">
          <span class="fs-5" i18n>Gutschein</span>
          <div *ngIf="dataService.cart.coupon as coupon" class="fs-6">
            <div *ngIf="!coupon.valid" class="text-danger">
              <span>{{coupon.code}}: </span>
              <span *ngFor="let error of coupon.errors"> {{error}} </span>
            </div>
            <div class="text-muted" *ngIf="coupon.valid && coupon.value">
              {{coupon.code}}: <span class="text-success">{{coupon.value | currency}}</span>
            </div>
          </div>
        </div>
        <button type="button"
                *ngIf="!readonly"
                class="btn btn-rounded-icon btn-light-info me-2 flex-shrink-0 " (click)="editCoupon()">
          <span class="material-icons md-24">edit</span>
        </button>
      </div>

      <div class="d-flex justify-content-between align-items-center py-1">
        <div class="d-flex flex-column">
          <span class="fs-5" i18n>Notiz</span>
          <span *ngIf="dataService.cart.note as note" class="mw-256px text-muted text-truncate fs-6">
            {{note}}
          </span>
        </div>

        <button type="button"
                *ngIf="!readonly"
                class="btn btn-rounded-icon btn-light-info me-2" (click)="editNote()">
          <span class="material-icons md-24">edit</span>
        </button>
      </div>

      <div class="d-flex justify-content-between align-items-center py-1" *appFeature="FEATURE_FLAGS.MAIL_RECEIPTS">
        <div class="d-flex flex-column">
          <span class="fs-5" i18n>Beleg per E-Mail</span>
          <span *ngIf="dataService.cart.customer?.email" class="text-muted fs-6">
            <span *ngIf="dataService.cart.customer.acceptsNewsletter" class="material-icons text-success fs-6">check</span>
            <span *ngIf="!dataService.cart.customer.acceptsNewsletter" class="material-icons text-danger fs-6">close</span>
            {{dataService.cart.customer.email}}
          </span>
        </div>
        <button type="button"
                *ngIf="!readonly"
                class="btn btn-rounded-icon btn-light-info me-2 flex-shrink-0" (click)="editRecipient()">
          <span class="material-icons md-24">edit</span>
        </button>
      </div>

      <app-customer-item *appFeature="FEATURE_FLAGS.CUSTOMERS"
        [customer]="dataService.cart.customer"
        [readonly]="readonly"
        (attachCustomer)="attachCustomer()">
      </app-customer-item>

      <div class="d-flex justify-content-between align-items-center py-1 me-2 h-aligned">
        <span class="fs-5" i18n>Zwischensumme EUR</span>
        <span class="fs-5 text-info">{{dataService.cart.subtotalPrice | currency}}</span>
      </div>

      <div class="d-flex justify-content-between align-items-center py-1 small text-muted me-2"
           *ngFor="let tax of dataService.cart.taxes">
        <span i18n>MWSt ({{tax.rate}}%)</span>
        <span>{{tax.amount | currency}}</span>
      </div>
    </div>
  `,
    styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
    .separated > *:not(:last-child) {
      border-bottom: solid 1px var(--bs-gray-400);
    }
    .h-aligned {
      height: 53px;
    }
  `],
    standalone: false
})
export class CartContentComponent implements OnDestroy {
  @Input() showItemCounters = false;
  @Input() readonly = false;

  FEATURE_FLAGS = FEATURE_FLAGS;

  destroy = new Subject<void>();

  constructor(
    public dataService: DataService,
    private readonly modalService: NgbModal,
    private readonly pusher: PusherService,
    private readonly barcodeFeedback: FeedbackService,
    private readonly scanner: BarcodeScannerService) {
    merge(this.pusher.scan, this.scanner.scannerResults).pipe(takeUntil(this.destroy)).subscribe(async (scanned) => {
      const productId = this.dataService.barcodes[scanned];

      if (productId) {
        await this.dataService.addToCart(productId);
        this.barcodeFeedback.showBarcodeSuccess();
      } else {
        this.barcodeFeedback.showBarcodeError();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  handleItemAction({ item, action }: CartItemAction) {
    if (this.readonly) {
      return;
    }
    if (action === 'edit') {
      this.updateItem(item);
    } else {
      throw new Error(`Unhandled action received for cart item: ${action}.`);
    }
  }

  editCoupon() {
    if (this.readonly) {
      return;
    }
    this.modalService.open(CouponModalComponent);
  }

  editNote() {
    if (this.readonly) {
      return;
    }
    this.modalService.open(NotesModalComponent);
  }

  editRecipient() {
    if (this.readonly) {
      return;
    }
    this.modalService.open(CustomerModalComponent);
  }

  private updateItem(item: CartItem) {
    const modal = this.modalService.open(CartItemEditComponent);
    modal.componentInstance.lineItem = item;
  }

  attachCustomer() {
    if (this.readonly) {
      return;
    }
    this.modalService.open(CustomerSelectorComponent);
  }
}
